import React from "react";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useReveal, revealClass } from "../hooks/useReveal";

const PORTRAIT_URL =
  "https://customer-assets.emergentagent.com/job_strategic-builder-5/artifacts/ivz19zlf_WhatsApp%20Image%202026-06-12%20at%2022.39.57.jpeg";

export const Hero = () => {
  const { t } = useLanguage();
  const [eyebrowRef, eyebrowVisible] = useReveal();
  const [titleRef, titleVisible] = useReveal();
  const [bodyRef, bodyVisible] = useReveal();
  const [imageRef, imageVisible] = useReveal();

  return (
    <section
      data-testid="section-hero"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 hero-texture overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-y-14 md:gap-x-12 items-center">
          {/* Left: Typography column */}
          <div className="col-span-12 lg:col-span-7 order-2 lg:order-1">
            <div ref={eyebrowRef} className={revealClass(eyebrowVisible)}>
              <p className="eyebrow mb-6" data-testid="hero-eyebrow">
                {t.hero.eyebrow}
              </p>
            </div>

            <h1
              ref={titleRef}
              className={`${revealClass(titleVisible)} font-serif-display font-light tracking-tightest text-ink leading-[0.92] text-[56px] sm:text-[80px] md:text-[96px] lg:text-[104px] xl:text-[120px]`}
              data-testid="hero-name"
            >
              Ivan
              <br />
              <span className="text-navy">Bautista</span>
            </h1>

            <div
              ref={bodyRef}
              className={`${revealClass(bodyVisible)} mt-8 md:mt-10 max-w-xl`}
            >
              <p
                className="font-serif-display text-2xl md:text-3xl text-ink-soft leading-snug mb-5"
                data-testid="hero-subtitle"
              >
                {t.hero.subtitle}
              </p>
              <p
                className="text-base md:text-lg text-ink-soft leading-relaxed font-light"
                data-testid="hero-body"
              >
                {t.hero.body}
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href="#projects"
                  data-testid="hero-cta-projects"
                  className="group inline-flex items-center justify-center gap-3 bg-navy text-white px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-navy-light transition-colors"
                >
                  {t.hero.ctaProjects}
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
                <a
                  href="#contact"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center justify-center gap-3 border border-ink text-ink px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-ink hover:text-white transition-colors"
                >
                  {t.hero.ctaContact}
                </a>
              </div>
            </div>
          </div>

          {/* Right: Portrait */}
          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2">
            <figure
              ref={imageRef}
              className={`${revealClass(imageVisible)} relative`}
              data-testid="hero-portrait"
            >
              <div className="editorial-img bg-ink aspect-[4/5] w-full max-w-[520px] mx-auto lg:ml-auto lg:mr-0 overflow-hidden">
                <img
                  src={PORTRAIT_URL}
                  alt="Ivan Bautista — retrato"
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <figcaption className="mt-4 flex items-center gap-3 max-w-[520px] mx-auto lg:ml-auto lg:mr-0">
                <span className="h-px w-10 bg-ink-soft/40" />
                <span className="eyebrow !text-ink-soft">
                  Girona · {t.hero.year}
                </span>
              </figcaption>
            </figure>
          </div>
        </div>

        {/* Bottom stat strip */}
        <div className="hidden md:grid mt-20 md:mt-24 grid-cols-12 border-t border-line pt-8">
          <div className="col-span-3">
            <p className="eyebrow mb-2">{t.hero.meta}</p>
            <p
              className="font-serif-display text-3xl text-ink leading-none"
              data-testid="hero-meta-year"
            >
              {t.hero.year}
            </p>
          </div>
          <Stat label="Proyectos" value="3 +" className="col-span-3" />
          <Stat
            label="Ámbitos"
            value="Deporte · Energía · Comunidad"
            className="col-span-3"
          />
          <Stat label="Base" value="Girona, España" className="col-span-3" />
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value, className = "" }) => (
  <div className={className}>
    <p className="eyebrow mb-2">{label}</p>
    <p className="text-sm text-ink font-medium">{value}</p>
  </div>
);

export default Hero;
